import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';

export interface ParserMetrics {
  today_parsed: number;
  today_avg_confidence: number;
  needs_review: number;
  needs_review_avg_confidence: number;
  failed: number;
  avg_processing_time_ms: number;
  avg_cost: number;
  vendor_stats: {
    vendor: string;
    parsed_count: number;
    avg_confidence: number;
    success_rate: number;
    avg_time_ms: number;
    avg_cost: number;
  }[];
}

export const useParserMetrics = () => {
  const { user } = useAuth();

  const metricsQuery = useQuery({
    queryKey: ['parser-metrics', user?.id],
    queryFn: async () => {
      if (!user?.id) return null;

      // Query extraction sessions for today's metrics
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const { data: sessions, error } = await supabase
        .from('bill_extraction_sessions')
        .select('*')
        .eq('user_id', user.id)
        .gte('created_at', today.toISOString());

      if (error) throw error;

      // Calculate metrics from sessions
      const parsed = sessions?.filter(s => s.status === 'completed') || [];
      const needsReview = sessions?.filter(s => 
        s.status === 'completed' && 
        s.confidence_scores && 
        (s.confidence_scores as any).overall_confidence < 0.85
      ) || [];
      const failed = sessions?.filter(s => s.status === 'failed') || [];

      const avgConfidence = parsed.length > 0
        ? parsed.reduce((sum, s) => {
            const conf = s.confidence_scores as any;
            return sum + (conf?.overall_confidence || 0);
          }, 0) / parsed.length
        : 0;

      const avgReviewConfidence = needsReview.length > 0
        ? needsReview.reduce((sum, s) => {
            const conf = s.confidence_scores as any;
            return sum + (conf?.overall_confidence || 0);
          }, 0) / needsReview.length
        : 0;

      const avgTime = parsed.length > 0
        ? parsed.reduce((sum, s) => sum + (s.processing_time_ms || 0), 0) / parsed.length
        : 0;

      // Fetch vendor stats by joining sessions with bills
      const { data: billsData } = await supabase
        .from('bill_extraction_sessions')
        .select(`
          *,
          bills!bill_extraction_sessions_bill_id_fkey(supplier_name)
        `)
        .eq('user_id', user.id)
        .gte('created_at', today.toISOString());

      // Calculate vendor statistics
      const vendorMap = new Map<string, {
        count: number;
        totalConfidence: number;
        successCount: number;
        totalTime: number;
      }>();

      billsData?.forEach(session => {
        const vendor = (session.bills as any)?.supplier_name || 'Unknown';
        const current = vendorMap.get(vendor) || {
          count: 0,
          totalConfidence: 0,
          successCount: 0,
          totalTime: 0,
        };

        current.count++;
        if (session.status === 'completed') {
          current.successCount++;
          const conf = (session.confidence_scores as any)?.overall_confidence || 0;
          current.totalConfidence += conf;
        }
        current.totalTime += session.processing_time_ms || 0;

        vendorMap.set(vendor, current);
      });

      const vendorStats: ParserMetrics['vendor_stats'] = Array.from(vendorMap.entries())
        .map(([vendor, stats]) => ({
          vendor,
          parsed_count: stats.count,
          avg_confidence: stats.successCount > 0 ? stats.totalConfidence / stats.successCount : 0,
          success_rate: stats.count > 0 ? stats.successCount / stats.count : 0,
          avg_time_ms: stats.count > 0 ? stats.totalTime / stats.count : 0,
          avg_cost: 0.02, // Placeholder
        }))
        .sort((a, b) => b.parsed_count - a.parsed_count)
        .slice(0, 10); // Top 10 vendors

      return {
        today_parsed: parsed.length,
        today_avg_confidence: avgConfidence,
        needs_review: needsReview.length,
        needs_review_avg_confidence: avgReviewConfidence,
        failed: failed.length,
        avg_processing_time_ms: avgTime,
        avg_cost: 0.0234, // Placeholder
        vendor_stats: vendorStats,
      };
    },
    enabled: !!user?.id,
    refetchInterval: 30000, // Refresh every 30 seconds
  });

  return {
    metrics: metricsQuery.data,
    isLoading: metricsQuery.isLoading,
    error: metricsQuery.error,
  };
};
